import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Shared hook for auto-pricing from price lists in Quotes & Invoices.
 * Handles: price list detection, auto-pricing, volume discounts, override tracking.
 */
export function usePriceListPricing(customerId, items, setItems) {
  const [priceListInfo, setPriceListInfo] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const prevCustomerRef = useRef(customerId);

  // Fetch customer's price list when customer changes
  useEffect(() => {
    if (!customerId) {
      setPriceListInfo(null);
      return;
    }
    api.get(`/customers/${customerId}/price-list`)
      .then(r => {
        if (r.data.price_list_id) {
          setPriceListInfo(r.data);
        } else {
          setPriceListInfo(null);
        }
      })
      .catch(() => setPriceListInfo(null));
  }, [customerId]);

  // When customer changes, re-price all existing product-based items
  useEffect(() => {
    if (prevCustomerRef.current === customerId) return;
    prevCustomerRef.current = customerId;
    if (!customerId || items.length === 0) return;
    const productItems = items.filter(i => i.product_id);
    if (productItems.length === 0) return;

    setLoadingPrices(true);
    Promise.all(
      productItems.map(item =>
        api.get("/price-lists/get-price", {
          params: { product_id: item.product_id, customer_id: customerId, quantity: item.quantity || 1 }
        }).then(r => ({ item_id: item.item_id, ...r.data })).catch(() => null)
      )
    ).then(results => {
      setItems(prev => prev.map(item => {
        const r = results.find(x => x && x.item_id === item.item_id);
        if (!r) return item;
        const newPrice = r.source !== "base" ? r.price : item.unit_price;
        const newSource = r.source !== "base" ? (r.price_list || "") : "";
        return {
          ...item,
          unit_price: newPrice,
          list_price: newPrice,
          price_source: newSource,
          is_override: false,
          override_reason: "",
        };
      }));
      if (results.some(r => r && r.source !== "base")) {
        toast.success("Prices updated from customer's price list");
      }
    }).finally(() => setLoadingPrices(false));
  }, [customerId, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch price for a product (used on add + quantity change)
  const fetchPrice = useCallback(async (productId, quantity) => {
    if (!productId || !customerId) return null;
    try {
      const res = await api.get("/price-lists/get-price", {
        params: { product_id: productId, customer_id: customerId, quantity: quantity || 1 }
      });
      return res.data;
    } catch {
      return null;
    }
  }, [customerId]);

  // Re-price a single item when quantity changes (volume discount trigger)
  const onQuantityChange = useCallback((idx, newQty) => {
    const item = items[idx];
    
    // CRITICAL: Update quantity immediately for responsive UI
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: newQty };
      return next;
    });
    
    // Then fetch new price asynchronously (don't block UI)
    if (!item?.product_id || !customerId) return;
    const qty = parseFloat(newQty) || 1;
    
    fetchPrice(item.product_id, qty).then(priceData => {
      if (!priceData || priceData.source === "base") return;
      const newPrice = priceData.price;
      const oldListPrice = parseFloat(item.list_price) || 0;
      
      if (newPrice !== oldListPrice) {
        setItems(prev => {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            unit_price: newPrice,
            list_price: newPrice,
            price_source: priceData.price_list || next[idx].price_source,
            is_override: false,
            override_reason: "",
          };
          return next;
        });
        if (priceData.source.includes("volume_discount")) {
          toast.success(`Volume discount applied: ${priceData.source.replace("volume_discount ", "")}`);
        }
      }
    });
  }, [items, customerId, fetchPrice, setItems]);

  // Detect price override when user manually changes unit_price
  const onPriceChange = useCallback((idx, newPriceStr) => {
    const newPrice = parseFloat(newPriceStr) || 0;
    const item = items[idx];
    const listPrice = parseFloat(item?.list_price) || 0;
    const isOverride = listPrice > 0 && Math.abs(newPrice - listPrice) > 0.005;
    setItems(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        unit_price: newPriceStr,
        is_override: isOverride,
        override_reason: isOverride ? (next[idx].override_reason || "") : "",
      };
      return next;
    });
  }, [items, setItems]);

  // Check if there are any unresolved overrides (missing reasons)
  const hasUnresolvedOverrides = items.some(i => i.is_override && !i.override_reason?.trim());
  const overrideCount = items.filter(i => i.is_override).length;

  return {
    priceListInfo,
    loadingPrices,
    fetchPrice,
    onQuantityChange,
    onPriceChange,
    hasUnresolvedOverrides,
    overrideCount,
  };
}
